import { ApolloQueryResult } from '@apollo/client';
import { addDays, format } from 'date-fns';
import get from 'lodash/get';
import set from 'lodash/set';
import toNumber from 'lodash/toNumber';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import { DatePicker } from '@atlaskit/datetime-picker';
import Form, { ErrorMessage, Field, FormApi, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import {
  BenefitCustomisableValue,
  EntitlementDetails,
  PricingType,
  Promotion,
} from '@css-xp/graphtypes/src/generated-types-new';
import { updateEntitlementPlan } from '@atlassian/temp-proxies';
import { EntitlementBaseProduct } from '@atlassian/cssxp-types-entitlement';
import { PromotionDuration } from '@atlassian/cssxp-types-promotion';
import { OrderRequestMetaData, UpdateOrderRequest, EndsAtType } from '@atlassian/cssxp-typescommon';
import { getUserTier, isAnnualTermedSubscription } from '@atlassian/cssxp-utils-entitlement';
import { ApprovalEntityInput, ApprovalType, EntityType, useApprovalDeskPermissions } from '@css-xp/apd-common';
import { ApprovalFormSection, useApprovals } from '@css-xp/apd-process-manager';
import {
  ErrorComponents,
  InfoPanel,
  NotificationTypes,
  successNotificationBuilder,
  useFormSubmit,
  useNotification,
  useSidebar,
  useFeatureToggle,
} from '@css-xp/base';
import { ENABLE_ANNUAL_TERMS } from '@atlassian/cssxp-utils-generic';
import { getChargeElement } from '@css-xp/base/entitlement-utils';
import { isStatusPageTrial } from '../../../../entitlement-page-utility/entitlement-summary/entitlment-summary-utils';
import { getCurrentTotalTrialLength } from './extend-trial-utils';

enum PromotionType {
  TRIAL_EXTENSION = 'TRIAL_EXTENSION',
}
interface Props {
  entitlement: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

interface FormData {
  customValue?: number;
  supportTicketLink?: string;
  handNote?: string;
  comments?: string;
}

const constructRequestData = ({
  entitlementId,
  formData,
  formApi,
  customTrialPromotion,
  entitlement,
  isAnnualTermEnabled,
}: {
  entitlementId: EntitlementDetails['id'];
  formData: FormData;
  formApi: FormApi<FormData>;
  customTrialPromotion: Promotion;
  entitlement: EntitlementDetails;
  isAnnualTermEnabled: boolean;
}) => {
  const transformedData: FormData = {};
  // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
  formApi.getRegisteredFields().forEach((key: any) => {
    // get the value from the field state or the form data
    const value = formApi.getFieldState(key)?.value ?? get(formData, key);
    set(transformedData, key, value);
  });

  const requestData: UpdateOrderRequest = {
    entitlementId,
    metadata: {
      [OrderRequestMetaData.SUPPORT_TICKET_NUMBER]: formData.supportTicketLink ?? null,
      [OrderRequestMetaData.HAND_NOTE]: formData.handNote ?? null,
    },
    // entitlement will be in trial mode, so sending skipTrial as false
    skipTrial: false,
    isPromotionStackingAllowed: true,
    productId: entitlement?.productId || '',
    units: getUserTier(entitlement?.subscription)?.toString(),
    chargeElement: getChargeElement(entitlement?.productId as string),
    endsAt: isAnnualTermedSubscription(entitlement, isAnnualTermEnabled)
      ? {
          type: EndsAtType.TIMESTAMP,
          value: entitlement?.endTimestamp as number,
        }
      : undefined,
    nextBillingAnchorTimestamp: entitlement?.nextBillingTimestamp ? `${entitlement?.nextBillingTimestamp}` : undefined,
  };

  const promotionIdToAdd = customTrialPromotion?.id;
  const promotionsToAdd = promotionIdToAdd ? [promotionIdToAdd] : [];
  requestData.promotionsToAdd = promotionsToAdd;
  const customisedValues = {
    applicationReason: null,
    benefits: [
      {
        value: transformedData.customValue,
        duration: PromotionDuration.ONCE,
        iterations: customTrialPromotion?.benefits?.[0]?.iterations || undefined,
      },
    ],
  };
  requestData.addPromotions = [
    {
      promotionDefinition: {
        promotionId: customTrialPromotion?.id ?? '',
        customisedValues: customisedValues,
      },
    },
  ];
  // Indicates an upgradeTrial order . Backend OMS engine utilizes this flag to identify which pricingplanId should be used .
  if (entitlement.upgradeTrial) {
    requestData.isUpgradeTrialOrder = !!entitlement.upgradeTrial;
  }
  return requestData;
};

const getCustomTrialPromotion = (promotions: any) => {
  return (
    promotions?.filter((promotion: any) => {
      // Note: The `.includes` method is case-sensitive
      return (
        promotion?.isPromotionCustomisable &&
        promotion?.pricingType?.includes(PricingType.LIMITED_FREE) &&
        promotion?.type === PromotionType.TRIAL_EXTENSION
      );
    })?.[0] ?? null
  );
};

// TODO: perhaps will remove these default values once customisable is available in promotion object.
const DefaultValueOption = {
  type: 'range',
  lowerBound: 1,
  upperBound: 365,
};

enum DefaultBoundvalue {
  LOWER_BOUND = 0,
  UPPER_BOUND = 365,
}

export const EntitlementExtendTrialPane = ({ entitlement, refetch }: Props) => {
  const {
    id: entitlementId,
    entitlementNumber,
    availablePromotions,
    productName,
    edition,
    offering,
    isApp,
    upgradeTrial,
  } = entitlement || {};
  const { closeSidebar } = useSidebar();
  const [isEnabled] = useFeatureToggle();
  const isAnnualTermEnabled = isEnabled(ENABLE_ANNUAL_TERMS);
  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<UpdateOrderRequest, any>({
    submitFormAction: updateEntitlementPlan,
    onComplete: () => {
      closeSidebar();
      refetch();
    },
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const onApprovalComplete = React.useCallback(() => {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('approval-submit-success')
      .withHeader('Request submitted')
      .withMessage(
        <div>
          The request has been created. You can view your request in the <strong>approval requests</strong> tab. <br />
          <Link to={`#approvals`}>View approval request</Link>
        </div>
      )
      .withDismissAfter(5000)
      .isVersion(NotificationTypes.FLAG)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    refetch?.();
  }, [closeSidebar, refetch, sendSuccessNotification, setNotificationSent]);

  const { isTrialExtApprovalFlowEnabled } = useApprovalDeskPermissions();
  const {
    approvalList,
    approvalError,
    submitApproval,
    approvalProcessing,
    evaluate: evaluateApproval,
    approvalRequired,
    approvalSlackChannel,
    setApprovalRequired,
  } = useApprovals({
    approvalType: ApprovalType.TRIAL_EXTENSION,
    onApprovalComplete,
  });

  const validator = (value: any, validatorRef: BenefitCustomisableValue) => {
    if (!Number.isInteger(toNumber(value))) {
      return 'Must be a number without decimal point';
    }
    if (toNumber(value) < toNumber(validatorRef?.lowerBound) || toNumber(value) > toNumber(validatorRef?.upperBound)) {
      return `Value cannot be less than ${validatorRef?.lowerBound ?? DefaultBoundvalue.LOWER_BOUND} and greater than ${
        validatorRef?.upperBound ?? DefaultBoundvalue.UPPER_BOUND
      }`;
    }
    return undefined;
  };

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('Extend-trial-success')
      .withHeader('Trial extended successfully')
      .withMessage('You can view the change in the order history tab.')
      .withDismissAfter(5000)
      .isVersion(NotificationTypes.FLAG)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  function getNumberOfDays(start: string | null | undefined, end: string | null | undefined) {
    const date1 = new Date(start ?? '');
    const date2 = new Date(end ?? '');
    const diffInTime = date2.getTime() - date1.getTime();
    const diffInDays = Math.round(diffInTime / (1000 * 60 * 60 * 24));
    return diffInDays || 0;
  }

  const changeNoOfDays = (e: string) => {
    const days = getNumberOfDays(trialEndDate, e);
    setDays(days.toString());
  };

  const changeExpiryDate = (e: React.FormEvent<HTMLInputElement>) => {
    const date = format(addDays(new Date(trialEndDate), Number(e.currentTarget.value)), 'MMM dd, yyyy');
    setNewExpiryDate(date);
  };

  const getTrialEndDate = () => {
    const isStatusPage = entitlement.baseProduct === EntitlementBaseProduct.STATUS_PAGE;
    if (!isStatusPage && entitlement?.subscriptionTimestamps?.lastTrialEndTimestamp) {
      return entitlement?.subscriptionTimestamps?.lastTrialEndTimestamp;
    }
    if (isStatusPageTrial(entitlement) && entitlement?.latestPeriodEndDate) {
      return entitlement?.latestPeriodEndDate;
    }
    return '';
  };

  const customTrialPromotion = getCustomTrialPromotion(availablePromotions);
  const firstBenefitItem = customTrialPromotion?.benefits?.[0];
  const customValueOptions = firstBenefitItem?.customisable?.value ?? DefaultValueOption;
  const [days, setDays] = useState(isApp ? 30 : firstBenefitItem?.value ?? 0);
  const trialEndDate = upgradeTrial?.endTimestamp ? upgradeTrial.endTimestamp : getTrialEndDate();
  const trialDateFromBenifitItem = format(addDays(new Date(trialEndDate), Number(days)), 'MMM dd, yyyy');
  const [newExpiryDate, setNewExpiryDate] = useState(trialDateFromBenifitItem);

  const handleFormSubmit = async (formData: FormData, formApi: FormApi<FormData>) => {
    const requestData = constructRequestData({
      entitlementId,
      formData,
      formApi,
      customTrialPromotion,
      entitlement,
      isAnnualTermEnabled,
    });
    const standardTrialLength = offering?.trial?.lengthDays ?? 0;
    const trialExtensionDaysRequested = Number(formData.customValue);
    const currentTotalTrialLength = getCurrentTotalTrialLength(entitlement);
    const netTrialDays = currentTotalTrialLength
      ? currentTotalTrialLength + trialExtensionDaysRequested - standardTrialLength
      : null;
    const entity: ApprovalEntityInput = {
      entityType: EntityType.ENTITLEMENT,
      entityId: entitlementId,
      entityNumber: entitlementNumber,
      currentTotalTrialLength,
      trialExtensionDaysRequested,
      netTrialDays,
      standardTrialLength,
      productName,
      edition,
      isApp,
    };
    if (approvalRequired) {
      submitApproval({
        approvalType: ApprovalType.TRIAL_EXTENSION,
        businessKey: entitlementNumber as string,
        entityInformation: [entity],
        gsacId: formData.supportTicketLink,
        initiatorComments: formData.comments,
        additionalInformation: requestData,
      });
    } else {
      if (isTrialExtApprovalFlowEnabled) {
        const evaluation = await evaluateApproval([entity]);
        if (evaluation && !evaluation.isApprovalRequired && !evaluation.error) {
          submitForm(requestData as UpdateOrderRequest);
        }
      } else {
        submitForm(requestData as UpdateOrderRequest);
      }
    }
  };

  return (
    <div style={{ maxWidth: '600px' }}>
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title="Extend trial"></FormHeader>
            <FormSection>
              {!!formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="Unable to extend the trial due to the following reason"
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {!!approvalError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="Unable to extend the trial due to the following reason"
                      message={approvalError.message}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              <div>
                <Field
                  name="customValue"
                  label="Extend for (days)"
                  isRequired={true}
                  validate={(value: any) => validator(value, customValueOptions)}
                  defaultValue={days?.toString()}
                  isDisabled={isApp || approvalRequired}
                >
                  {({ fieldProps, error }) => (
                    <>
                      <Textfield
                        {...fieldProps}
                        name="customValue"
                        testId="customValue-field"
                        onChange={(e) => {
                          changeExpiryDate(e);
                          fieldProps.onChange(e);
                        }}
                      />
                      {error && <ErrorMessage>{error}</ErrorMessage>}
                    </>
                  )}
                </Field>
                <Field
                  name="extendExpiryDate"
                  label="Or extend to (date)"
                  defaultValue={newExpiryDate ? new Date(newExpiryDate).toDateString() : new Date().toDateString()}
                >
                  {({ fieldProps }) => (
                    <div className="cal-field">
                      <DatePicker
                        {...fieldProps}
                        testId="expiry-date"
                        dateFormat="MMM DD, YYYY"
                        isDisabled={isApp || approvalRequired}
                        onChange={(e) => {
                          changeNoOfDays(e);
                          fieldProps.onChange(e);
                        }}
                      />
                    </div>
                  )}
                </Field>
              </div>
              <Field
                label={'Support ticket number'}
                name="supportTicketLink"
                isRequired={true}
                validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
              >
                {({ fieldProps, error }) => (
                  <>
                    <Textfield
                      className="support-ticket"
                      {...fieldProps}
                      name={'supportTicketLink'}
                      placeholder="Add GSAC ticket number"
                      testId="supportTicketLink-field"
                    />
                    {error && <ErrorMessage testId="supportTicketLink-error">Enter a valid ticket number</ErrorMessage>}
                  </>
                )}
              </Field>
              <Field label={'Comment if any'} name="handNote">
                {({ fieldProps }) => (
                  <Textfield {...fieldProps} name="handNote" testId="hand-note-field" placeholder="Add comment" />
                )}
              </Field>
            </FormSection>
            {approvalRequired ? (
              <ApprovalFormSection
                approvalType={ApprovalType.TRIAL_EXTENSION}
                approvalItems={approvalList}
                slackChannel={approvalSlackChannel as string}
                onCancel={() => closeSidebar()}
                isLoading={approvalProcessing}
                onEdit={setApprovalRequired}
              />
            ) : (
              <FormFooter align="end">
                <ButtonGroup>
                  <Button appearance="subtle" onClick={() => closeSidebar()} testId="cancel-trial-btn">
                    Cancel
                  </Button>
                  <LoadingButton
                    appearance="primary"
                    type="submit"
                    isLoading={isProcessing || approvalProcessing}
                    testId="submit-trial-btn"
                  >
                    Submit
                  </LoadingButton>
                </ButtonGroup>
              </FormFooter>
            )}
          </form>
        )}
      </Form>
    </div>
  );
};
